import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { fmtFCFA, formatApiError, clean } from "@/lib/api";
import { differenceInCalendarDays, format, parseISO, subDays, startOfToday } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Users, Star, ShieldCheck } from "lucide-react";

const PAYMENTS = ["À l'arrivée", "Mobile Money", "Virement bancaire", "Espèces au bureau"];

export const BookingWidget = ({ property }) => {
  const p = property;
  const [range, setRange] = useState(undefined);
  const [guests, setGuests] = useState(1);
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [payment, setPayment] = useState(PAYMENTS[0]);
  const [occupied, setOccupied] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/properties/${p.id}/availability`); setOccupied(data || []); } catch {}
    })();
  }, [p.id]);

  const disabled = useMemo(() => {
    const list = [{ before: startOfToday() }];
    for (const r of occupied) {
      try { list.push({ from: parseISO(r.check_in), to: subDays(parseISO(r.check_out), 1) }); } catch {}
    }
    return list;
  }, [occupied]);

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;
  const nightly = p.price_night || 0;
  const subtotal = nights * nightly;
  const cleaning = p.cleaning_fee || 0;
  const servicePct = p.service_pct ?? 5;
  const service = Math.round(subtotal * servicePct / 100);
  const total = subtotal + cleaning + service;

  const book = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) { toast.error("Sélectionnez vos dates d'arrivée et de départ"); return; }
    if (nights < (p.min_nights || 1)) { toast.error(`Séjour minimum de ${p.min_nights || 1} nuit(s)`); return; }
    setSending(true);
    try {
      const { data } = await api.post("/reservations", clean({
        property_id: p.id, ...contact, guests: Number(guests), payment_method: payment,
        check_in: format(range.from, "yyyy-MM-dd"), check_out: format(range.to, "yyyy-MM-dd"),
      }));
      setDone(data);
      setOccupied((o) => [...o, { check_in: data.check_in, check_out: data.check_out, kind: "reserved" }]);
      toast.success("Réservation envoyée ! Nous vous confirmons rapidement.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="rounded-xl bg-white border border-black/5 p-6 text-center" data-testid="booking-success">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100"><ShieldCheck className="h-7 w-7 text-green-600" /></div>
        <h3 className="mt-4 font-serif text-2xl font-semibold text-navy">Demande envoyée</h3>
        <p className="mt-1 font-mono text-sm text-gold">{done.reference}</p>
        <p className="mt-3 text-sm text-slate-600">{done.nights} nuit(s) · du {done.check_in} au {done.check_out}</p>
        <p className="mt-1 text-lg font-bold text-navy">{fmtFCFA(done.total)}</p>
        <p className="mt-3 text-xs text-slate-500">Un conseiller IMmOMAXE vous contactera pour confirmer et convenir du règlement ({done.payment_method}).</p>
        <Button onClick={() => { setDone(null); setRange(undefined); }} variant="outline" className="mt-4 rounded-lg" data-testid="booking-new">Nouvelle réservation</Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white border border-black/5 p-5 shadow-sm" data-testid="booking-widget">
      <div className="flex items-end justify-between">
        <div className="font-mono text-2xl font-bold text-navy">{fmtFCFA(nightly)}<span className="text-sm font-normal text-slate-400"> / nuit</span></div>
        {p.rating != null && <span className="flex items-center gap-1 text-sm font-semibold text-navy"><Star className="h-4 w-4 fill-gold text-gold" /> {p.rating} <span className="font-normal text-slate-400">({p.reviews_count})</span></span>}
      </div>

      <form onSubmit={book} className="mt-4 space-y-4" data-testid="booking-form">
        <div className="rounded-lg border border-black/10 p-2">
          <Calendar mode="range" locale={fr} numberOfMonths={1} selected={range} onSelect={setRange}
            disabled={disabled} className="mx-auto" data-testid="booking-calendar" />
        </div>

        {nights > 0 && (
          <div className="rounded-lg bg-ivory px-3 py-2 text-center text-sm text-navy" data-testid="booking-dates-summary">
            {format(range.from, "d MMM", { locale: fr })} → {format(range.to, "d MMM yyyy", { locale: fr })} · <b>{nights} nuit(s)</b>
          </div>
        )}

        <div>
          <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Voyageurs {p.capacity ? `(max ${p.capacity})` : ""}</Label>
          <Input type="number" min="1" max={p.capacity || undefined} value={guests} onChange={(e) => setGuests(e.target.value)} className="mt-1.5" data-testid="booking-guests" />
        </div>

        <div className="grid grid-cols-1 gap-2">
          <Input required placeholder="Nom complet *" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="booking-name" />
          <Input required placeholder="Téléphone *" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="booking-phone" />
          <Input type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="booking-email" />
        </div>

        <div>
          <Label className="text-xs text-slate-500">Moyen de paiement souhaité</Label>
          <Select value={payment} onValueChange={setPayment}>
            <SelectTrigger className="mt-1.5" data-testid="booking-payment"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYMENTS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {nights > 0 && (
          <div className="space-y-2 border-t border-dashed pt-3 text-sm" data-testid="booking-breakdown">
            <div className="flex justify-between text-slate-600"><span>{fmtFCFA(nightly)} × {nights} nuit(s)</span><span>{fmtFCFA(subtotal)}</span></div>
            {cleaning > 0 && <div className="flex justify-between text-slate-600"><span>Frais de ménage</span><span>{fmtFCFA(cleaning)}</span></div>}
            <div className="flex justify-between text-slate-600"><span>Frais de service ({servicePct}%)</span><span>{fmtFCFA(service)}</span></div>
            <div className="flex justify-between border-t pt-2 font-bold text-navy"><span>Total</span><span data-testid="booking-total">{fmtFCFA(total)}</span></div>
          </div>
        )}

        <Button type="submit" disabled={sending} className="w-full bg-gold text-navy hover:bg-gold-light font-semibold h-11 rounded-lg" data-testid="booking-submit">
          {sending ? "Envoi..." : nights > 0 ? `Réserver · ${fmtFCFA(total)}` : "Réserver"}
        </Button>
        <p className="text-center text-xs text-slate-400">Vous ne serez pas débité en ligne. La réservation est confirmée par IMmOMAXE.</p>
      </form>
    </div>
  );
};
